import _, { toNumber } from 'lodash';
import type { z } from 'zod';
import { Subject } from '../common/enums';
import { SearchStringSchema } from '../common/schemas';
import { type TransformerConfig } from '../common/types';
import { createDecodeFromDbFn, createEncodeToDbFn, createTransformer } from '../common/utils';
import { StudentAttributes } from './constants';
import { DBStudentSchema, StudentSchema } from './schemas';

type Student = z.infer<typeof StudentSchema>;
type DBStudent = z.infer<typeof DBStudentSchema>;

export const StudentCodecConfig: TransformerConfig<typeof StudentSchema, typeof DBStudentSchema> = {
  name: 'Student',
  clientSchema: StudentSchema,
  dbSchema: DBStudentSchema,
  transformers: [
    // classes: [{ subject, classId, course, stream }] ↔ classes: { "subject#classId": { course, stream } }
    createTransformer<typeof StudentSchema, typeof DBStudentSchema>({
      encoder: {
        sourceFields: ['classes'] as const,
        encode: ({ classes }) => ({
          classes: Object.fromEntries(
            classes.map((val: Student['classes'][number]) => [
              `${val.subject}#${val.classId}`,
              {
                course: val.course,
                stream: val.stream,
              },
            ])
          ),
        }),
      },
      decoder: {
        sourceFields: ['classes'] as const,
        decode: ({ classes }) => ({
          classes: Object.entries(classes ?? {}).map(([compositeClassId, val]) => {
            const parts = compositeClassId.split('#');
            return {
              subject: parts[0] as Subject,
              classId: parts[1]!,
              ...(val as NonNullable<DBStudent['classes']>[string]),
            };
          }),
        }),
      },
    }),

    // courseStates: [{ subject, course, stream?, ... }] ↔
    //   courseStates: { "subject#course": { ... } } OR { "subject#course#stream": { ... } }
    // The DB key is the source of truth for identity: stream lives only in the key,
    // never in the value. On decode we reconstruct subject/course/stream from the key.
    createTransformer<typeof StudentSchema, typeof DBStudentSchema>({
      encoder: {
        sourceFields: ['courseStates'] as const,
        encode: ({ courseStates }) => {
          const entries = courseStates.map((val: Student['courseStates'][number]) => {
            const { stream, ...rest } = val;
            return [
              stream ? `${val.subject}#${val.course}#${stream}` : `${val.subject}#${val.course}`,
              rest,
            ] as const;
          });

          // Object.fromEntries keeps only the LAST entry for a colliding key — silently
          // dropping a course state's credits. Duplicate identities always mean corrupted
          // in-memory state, so fail loudly instead of losing money-critical data.
          const seenKeys = new Set<string>();
          for (const [key] of entries) {
            if (seenKeys.has(key)) {
              throw new Error(`Duplicate course state identity: ${key}`);
            }
            seenKeys.add(key);
          }

          return { courseStates: Object.fromEntries(entries) };
        },
      },
      decoder: {
        sourceFields: ['courseStates'] as const,
        decode: ({ courseStates }) => ({
          courseStates: Object.entries(courseStates).map(([compositeKey, val]) => {
            const parts = compositeKey.split('#');
            return {
              ...(val as DBStudent['courseStates'][string]),
              subject: parts[0] as Subject,
              course: parts[1]!,
              ...(parts.length === 3 ? { stream: parts[2] } : {}),
            };
          }),
        }),
      },
    }),

    // tutorials: [{ tutorialId, duration, course, startDatetime }] ↔ tutorials: { tutorialId: { duration, course, startDatetimeIdentifier } }
    createTransformer<typeof StudentSchema, typeof DBStudentSchema>({
      encoder: {
        sourceFields: ['tutorials'] as const,
        encode: ({ tutorials }) => ({
          tutorials: Object.fromEntries(
            tutorials.map((tutorial: Student['tutorials'][number]) => [
              tutorial.tutorialId,
              {
                duration: tutorial.duration,
                course: tutorial.course,
                startDatetimeIdentifier: tutorial.tutorialId,
              },
            ])
          ),
        }),
      },
      decoder: {
        sourceFields: ['tutorials'] as const,
        decode: ({ tutorials }) => ({
          tutorials: Object.entries(tutorials ?? {}).map(([tutorialId, val]) => {
            return {
              tutorialId,
              duration: (val as NonNullable<DBStudent['tutorials']>[string]).duration,
              course: (val as NonNullable<DBStudent['tutorials']>[string]).course,
              startDatetime: toNumber(tutorialId.split('#')[0]!),
            };
          }),
        }),
      },
    }),

    // Handle search string updates
    createTransformer<typeof StudentSchema, typeof DBStudentSchema>({
      encoder: {
        sourceFields: StudentAttributes.Search,
        // No dependencies declared: the parent* search fields are optional and
        // may be absent on a student. Recompute searchStr by falling back to the
        // existing student (then '') so a partial update touching one search field
        // doesn't require every other search field to be present.
        encode: (student, oldStudent) => {
          let searchStr = '';
          StudentAttributes.Search.forEach(attr => {
            const val = student?.[attr] ?? oldStudent?.[attr] ?? '';
            searchStr += SearchStringSchema.parse(val);
          });
          return { searchStr, ..._.pick(student, StudentAttributes.Search) };
        },
      },
    }),
  ],
};

export const encodeStudentToDb = createEncodeToDbFn(StudentCodecConfig);
export const decodeStudentFromDb = createDecodeFromDbFn(StudentCodecConfig);
